import { Button, DialogContent, DialogTitle, HelpText, Input, Label, Textarea } from '@atlas/ui-primitives'
import { type FormEvent, useEffect, useState } from 'react'

/** The server refuses a shorter reason; refused here too, before the request. */
export const MIN_IGNORE_REASON = 3

export interface IgnoreErrorDialogProps {
	open: boolean
	title: string
	submitting?: boolean
	onIgnore: (reason: string) => void
	onCancel: () => void
	/** What is being ignored; a problem by default. */
	heading?: string
	/** What ignoring it means, once a reason is given. */
	consequence?: string
}

/** Ignoring is a decision, so it asks for the reason, which the audit log keeps. */
export function IgnoreErrorDialog({
	open,
	title,
	submitting,
	onIgnore,
	onCancel,
	heading = 'Ignore this problem',
	consequence = 'It stays ignored while it recurs, and is resolved when its step next succeeds.',
}: IgnoreErrorDialogProps) {
	const [reason, setReason] = useState('')
	useEffect(() => {
		if (open) setReason('')
	}, [open])
	if (!open) return null

	const trimmed = reason.trim()
	const canSubmit = trimmed.length >= MIN_IGNORE_REASON && !submitting
	const submit = (event: FormEvent) => {
		event.preventDefault()
		if (canSubmit) onIgnore(trimmed)
	}

	return (
		<DialogContent size="md" isOpen={open} onOpenChange={(next) => (next ? undefined : onCancel())}>
			<form onSubmit={submit}>
				<DialogTitle>{heading}</DialogTitle>
				<p className="mt-2 text-ds-sm text-ds-text-muted">{title}</p>
				<div className="mt-4">
					<Label htmlFor="ignore-reason">Reason</Label>
					<Textarea id="ignore-reason" value={reason} onChange={(event) => setReason(event.target.value)} />
					<HelpText>
						{trimmed.length >= MIN_IGNORE_REASON ? consequence : 'Say why, so whoever reads the audit log later knows.'}
					</HelpText>
				</div>
				<div className="mt-6 flex justify-end gap-2">
					<Button variant="outline" onPress={onCancel}>
						Cancel
					</Button>
					<Button type="submit" isDisabled={!canSubmit}>
						{submitting ? 'Ignoring…' : 'Ignore'}
					</Button>
				</div>
			</form>
		</DialogContent>
	)
}

export interface SyncBundleDialogProps {
	open: boolean
	submitting?: boolean
	onSync: (referenceNumber: number) => void
	onCancel: () => void
}

/** Sync one bundle now, by the Workfront reference number of any of its projects. */
export function SyncBundleDialog({ open, submitting, onSync, onCancel }: SyncBundleDialogProps) {
	const [value, setValue] = useState('')
	useEffect(() => {
		if (open) setValue('')
	}, [open])
	if (!open) return null

	const referenceNumber = Number.parseInt(value, 10)
	const valid = /^\d+$/.test(value.trim()) && referenceNumber > 0
	const submit = (event: FormEvent) => {
		event.preventDefault()
		if (valid && !submitting) onSync(referenceNumber)
	}

	return (
		<DialogContent size="md" isOpen={open} onOpenChange={(next) => (next ? undefined : onCancel())}>
			<form onSubmit={submit}>
				<DialogTitle>Sync one bundle</DialogTitle>
				<div className="mt-4">
					<Label htmlFor="sync-reference">Workfront reference number</Label>
					<Input
						id="sync-reference"
						inputMode="numeric"
						value={value}
						autoFocus
						placeholder="34600"
						onChange={(event) => setValue(event.target.value)}
					/>
					{value.trim() && !valid ? (
						<HelpText variant="error">Digits only: the number Workfront shows on the project.</HelpText>
					) : (
						<HelpText>The brief's or any asset brief's number: the whole program is fetched.</HelpText>
					)}
				</div>
				<div className="mt-6 flex justify-end gap-2">
					<Button variant="outline" onPress={onCancel}>
						Cancel
					</Button>
					<Button type="submit" isDisabled={!valid || submitting}>
						{submitting ? 'Queuing…' : 'Sync'}
					</Button>
				</div>
			</form>
		</DialogContent>
	)
}
